import { useState } from 'react';
import { CheckIcon, DownloadIcon, Loader2Icon } from 'lucide-react';
import { useLanguage } from '@contexts/LanguageContext';
import { Alert, AlertDescription } from '@/components/lumen/alert';
import { Button } from '@/components/lumen/button';
import { apiUrl } from '@constants/url';
import ConsigneeImportPanel from '@/features/consignee-import/ConsigneeImportPanel';
import { downloadXlsx } from '@/features/consignee-import/download';
import type { ImportResult } from '@/features/consignee-import/types';
import { resolveFirstLoginCopy } from '../i18n';
import StepHeading from '../StepHeading';

interface StepConsigneeProps {
  /** Fires after a successful import; the wizard then offers Lanjut instead of only Lewati. */
  onImported: (result: ImportResult) => void;
  importedCount: number | null;
  /** Passed through to the import panel's apply lifecycle. */
  onApplyStateChange?: (state: 'start' | 'success' | 'error') => void;
}

/**
 * Step 2: template down, filled sheet up, review, import. Same panel the
 * Master page uses; the wizard only frames it.
 */
export default function StepConsignee({ onImported, importedCount, onApplyStateChange }: StepConsigneeProps) {
  const { language } = useLanguage();
  const t = (key: string, vars?: Record<string, string | number>) => resolveFirstLoginCopy(language, key, vars);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadXlsx(apiUrl.master.consignees.template, 'Template_Consignee_JARVIS.xlsx');
    } catch (error) {
      console.warn('[first-login] template download failed', error);
      setDownloadError(t('consignee.downloadFailed'));
    } finally {
      setDownloading(false);
    }
  };

  const card = (title: string, body: React.ReactNode) => (
    <div className="flex flex-col gap-2 rounded-panel border border-line bg-raised p-3.5">
      <p className="m-0 font-semibold text-fg">{title}</p>
      {body}
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      <StepHeading heading={t('consignee.heading')} lead={t('consignee.lead')} />

      {importedCount !== null && (
        <Alert variant="ok">
          <CheckIcon aria-hidden="true" />
          <AlertDescription>{t('consignee.imported', { n: importedCount })}</AlertDescription>
        </Alert>
      )}

      {card(
        t('consignee.card.template.title'),
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="m-0 min-w-60 flex-1 text-fg-muted text-sm">{t('consignee.card.template.body')}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            disabled={downloading}
            aria-busy={downloading || undefined}
          >
            {downloading ? (
              <Loader2Icon className="animate-spin" aria-hidden="true" />
            ) : (
              <DownloadIcon aria-hidden="true" />
            )}
            {t('consignee.card.template.button')}
          </Button>
          {downloadError && <p className="m-0 w-full text-err text-xs">{downloadError}</p>}
        </div>
      )}
      {card(
        t('consignee.card.upload.title'),
        <ConsigneeImportPanel mode="wizard" onImported={onImported} onApplyStateChange={onApplyStateChange} />
      )}
    </div>
  );
}
